import { useLanguage } from "@tlc/ls2pac/components/Language";
import { EResourceCheckoutResponse } from "./DownloadableCheckoutDialog";
import { useMemo } from "react";
import React from "react";
import { Stack, Typography } from "@mui/material";
import { ConfirmDialog } from "@tlc/ui/dialog/ConfirmDialog";
import { useDownloadNowBlocked } from "./useDownloadNowBlocked";
import { DownloadNowButton } from "./DownloadNowButton";
import { DialogWithDataProps } from "@tlc/ui/dialog/useDialog";

interface DownloadableCheckoutSuccessDialogProps
  extends DialogWithDataProps<EResourceCheckoutResponse> {}
export const DownloadableCheckoutSuccessDialog = ({
  open,
  onClose,
  data: response,
}: DownloadableCheckoutSuccessDialogProps) => {
  const { translate, languageCode } = useLanguage();
  const downloadNowBlocked = useDownloadNowBlocked();

  const formattedDate = useMemo(() => {
    if (!open || !response?.expirationDate) {
      return null;
    }

    // langaugeCode is included in dependency array to trigger an update when the page language changes
    // however we don't pass it to navigator.language because it can be a non-standard format
    return new Intl.DateTimeFormat(navigator.language).format(
      new Date(response.expirationDate),
    );
  }, [languageCode, open, response?.expirationDate]);

  if (!response) {
    return null;
  }

  if (downloadNowBlocked) {
    return (
      <ConfirmDialog
        id="download-now-blocked-dialog"
        open={open}
        onClose={onClose}
        useSingleAction
        headline={translate("checkoutEResourceDialogTitle")}
        confirmButtonText={translate("ok")}
        onConfirm={onClose}
      >
        <Stack gap={1}>
          <Typography>
            {translate("checkoutEResourceDateMessage")} {formattedDate}.
          </Typography>

          <Typography>
            {/* Due to the library's settings you cannot download this eResource on computers inside the network. */}
            {translate("eResourceStationLocationMessage")}
          </Typography>
          <Typography>
            {/* stationLocationCheckoutLaterEResourceSuccessMessage=You will be able to download later from Account Activity > Loans. */}
            {translate("stationLocationCheckoutLaterEResourceSuccessMessage")}
          </Typography>
        </Stack>
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      id="download-now-or-later-dialog"
      open={open}
      onClose={onClose}
      headline={translate("checkoutEResourceDialogTitle")}
      dismissButtonText={translate("eResourceCheckoutDownloadLaterButton")}
      confirmButton={(props) => (
        <DownloadNowButton
          {...props}
          buttonText={translate("eResourceCheckoutDownloadNowButton")}
          itemId={response.itemId}
          service={response.service}
          onClick={(_) => onClose()}
        />
      )}
    >
      <Stack gap={1}>
        <Typography>
          {translate("checkoutEResourceDateMessage")} {formattedDate}.
        </Typography>
        <Typography>
          {/* If you select Download Later, go to Account Activity > Loans when you are ready to download. */}
          {translate("checkoutLaterEResourceSuccessMessage")}
        </Typography>
      </Stack>
    </ConfirmDialog>
  );
};
